// src/components/visuals/ParabolaShape.js
import React, { useRef } from "react";
import { InlineMath } from "react-katex";

export default function ParabolaShape({
  aSign = 1,
  width = 560,
  height = 200,
  axisColor = "currentColor",
  curveColor = "#f59e0b",
  strokeWidth = 2.5,
  xRange = 6,
  yWindow = 6,

  showXAxis = true,
  showZeroTicks = true, // działa kontekstowo: 2 ticki dla "two", 1 tick dla "one", 0 dla "none"

  // tryb korzeni: "two" (dwa zera), "one" (styczność), "none" (brak zer)
  rootMode = "two",
  // pozycje korzeni / styczności w układzie zadania (nie piksele)
  rootLeft = -2,
  rootRight = 2,
  tangentX = 0,

  // podpisy (LaTeX lub plain)
  labelLeft,
  labelRight,
  labelLeftTex,
  labelRightTex,
  labelDy = 24,
  fontSize = 18,
  fontFamily = "KaTeX_Main, STIXGeneral, 'Times New Roman', serif",
  useMathMinus = true,

  // podświetlenie rozwiązania na osi ("between" | "outside")
  highlightMode,
  highlightColor = "#a78bfa",
  highlightLeftClosed = false,
  highlightRightClosed = false,
  pinRadius = 7,
  bandTopOffset = 28,
  tickLength = 5,
}) {
  /* ── układ odniesienia ─────────────────────────────────────────── */
  const XMIN = -xRange,
    XMAX = xRange;
  const YMIN = -yWindow,
    YMAX = yWindow;

  const PAD = 32;
  const leftX = PAD,
    rightX = width - PAD;
  const topY = PAD,
    bottomY = height - PAD;

  const mapX = (x) => leftX + ((x - XMIN) / (XMAX - XMIN)) * (rightX - leftX);
  const mapY = (y) => bottomY - ((y - YMIN) / (YMAX - YMIN)) * (bottomY - topY);

  const s = aSign >= 0 ? 1 : -1;
  const xAxisY = mapY(0);
  const AR = 10;

  /* ── kształt paraboli (dopasowany do przypadku ∆) ────────────────
     Używamy postaci wierzchołkowej: y = s * ((x - m)^2 + V),
     gdzie:
       - "two": y = s * ((x - m)^2 - d^2)  → zera w m±d,
       - "one": y = s * ((x - x0)^2)       → styczność w x0,
       - "none": y = s * ((x - m)^2 + d^2) → zawsze nad/pod OX.
  */
  let m = 0,
    dAbs = 2,
    vertShift = 0;
  if (rootMode === "two") {
    m = (rootLeft + rootRight) / 2;
    dAbs = Math.max(0.5, Math.abs(rootRight - rootLeft) / 2); // min 0.5 dla ładnego kształtu
    vertShift = -(dAbs * dAbs); // y = s*((x-m)^2 - d^2)
  } else if (rootMode === "one") {
    m = tangentX;
    vertShift = 0; // y = s*(x - m)^2
  } else {
    m = 0;
    dAbs = 2;
    vertShift = dAbs * dAbs; // y = s*((x-m)^2 + d^2)
  }

  const fnY = (x) => s * ((x - m) * (x - m) + vertShift);

  // sampling ścieżki
  const N = 140;
  let d = "";
  for (let i = 0; i <= N; i++) {
    const x = XMIN + (i / N) * (XMAX - XMIN);
    const y = fnY(x);
    const px = mapX(x);
    const py = mapY(y);
    d += i === 0 ? `M ${px} ${py}` : ` L ${px} ${py}`;
  }

  // pozycje „korzeni” (lub styczności) w pikselach – dla podpisów i highlightu
  const pL = rootMode === "two" ? mapX(rootLeft) : rootMode === "one" ? mapX(tangentX) : null;
  const pR = rootMode === "two" ? mapX(rootRight) : rootMode === "one" ? mapX(tangentX) : null;

  /* ── normalizacja minusa i stringów ────────────────────────────── */
  const normalizeMinus = (txt) =>
    useMathMinus ? String(txt ?? "").replace(/-/g, "−") : String(txt ?? "");

  const plainLeft = normalizeMinus(labelLeft);
  const plainRight = normalizeMinus(labelRight);

  /* ── pin + band (highlight) ─────────────────────────────────────── */
  const strokeW = 3;
  const halfW = strokeW / 2;
  const maskId = useRef(`p-mask-${Math.random().toString(36).slice(2)}`).current;

  const needHoleL = highlightMode && !highlightLeftClosed && rootMode !== "none";
  const needHoleR = highlightMode && !highlightRightClosed && rootMode !== "none";

  const Pin = ({ x, yTop, yAxis, closed }) => {
    const yEnd = closed ? yTop : yTop + pinRadius - halfW;
    return (
      <>
        <line x1={x} y1={yAxis} x2={x} y2={yEnd} stroke={highlightColor} strokeWidth={strokeW} />
        {closed ? (
          <circle cx={x} cy={yTop} r={pinRadius} fill={highlightColor} />
        ) : (
          <circle cx={x} cy={yTop} r={pinRadius} fill="none" stroke={highlightColor} strokeWidth={strokeW} />
        )}
      </>
    );
  };

  const bandTopY = xAxisY - bandTopOffset;

  const BandBetween = () => {
    if (rootMode !== "two") return null;
    const useMask = needHoleL || needHoleR;
    return (
      <>
        {useMask && (
          <defs>
            <mask id={maskId}>
              <rect x="0" y="0" width={width} height={height} fill="white" />
              {needHoleL && <circle cx={pL} cy={bandTopY} r={pinRadius + 1} fill="black" />}
              {needHoleR && <circle cx={pR} cy={bandTopY} r={pinRadius + 1} fill="black" />}
            </mask>
          </defs>
        )}

        <rect
          x={pL}
          y={bandTopY}
          width={Math.max(0, pR - pL)}
          height={xAxisY - bandTopY}
          fill={highlightColor}
          fillOpacity="0.45"
          stroke={highlightColor}
          strokeOpacity="0.7"
          strokeWidth="2"
          rx="6"
          {...(useMask ? { mask: `url(#${maskId})` } : {})}
        />

        <line
          x1={highlightLeftClosed ? pL : pL + pinRadius + halfW}
          y1={bandTopY}
          x2={highlightRightClosed ? pR : pR - pinRadius - halfW}
          y2={bandTopY}
          stroke={highlightColor}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        <Pin x={pL} yTop={bandTopY} yAxis={xAxisY} closed={highlightLeftClosed} />
        <Pin x={pR} yTop={bandTopY} yAxis={xAxisY} closed={highlightRightClosed} />
      </>
    );
  };

  const BandOutside = () => {
    if (rootMode !== "two") return null;
    const useMask = needHoleL || needHoleR;
    return (
      <>
        {useMask && (
          <defs>
            <mask id={maskId}>
              <rect x="0" y="0" width={width} height={height} fill="white" />
              {needHoleL && <circle cx={pL} cy={bandTopY} r={pinRadius + 1} fill="black" />}
              {needHoleR && <circle cx={pR} cy={bandTopY} r={pinRadius + 1} fill="black" />}
            </mask>
          </defs>
        )}

        <rect
          x={leftX}
          y={bandTopY}
          width={Math.max(0, pL - leftX)}
          height={xAxisY - bandTopY}
          fill={highlightColor}
          fillOpacity="0.45"
          stroke={highlightColor}
          strokeOpacity="0.7"
          strokeWidth="2"
          rx="6"
          {...(useMask ? { mask: `url(#${maskId})` } : {})}
        />

        <rect
          x={pR}
          y={bandTopY}
          width={Math.max(0, rightX - pR)}
          height={xAxisY - bandTopY}
          fill={highlightColor}
          fillOpacity="0.45"
          stroke={highlightColor}
          strokeOpacity="0.7"
          strokeWidth="2"
          rx="6"
          {...(useMask ? { mask: `url(#${maskId})` } : {})}
        />

        <line
          x1={highlightLeftClosed ? pL : pL - pinRadius - halfW}
          y1={bandTopY}
          x2={leftX}
          y2={bandTopY}
          stroke={highlightColor}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        <line
          x1={highlightRightClosed ? pR : pR + pinRadius + halfW}
          y1={bandTopY}
          x2={rightX}
          y2={bandTopY}
          stroke={highlightColor}
          strokeWidth={strokeW}
          strokeLinecap="round"
        />

        <Pin x={pL} yTop={bandTopY} yAxis={xAxisY} closed={highlightLeftClosed} />
        <Pin x={pR} yTop={bandTopY} yAxis={xAxisY} closed={highlightRightClosed} />
      </>
    );
  };

  /* ── KLUCZ: stabilne renderowanie ułamków bez foreignObject ───────── */

  // Czy latex to liczba prosta (np. "-4", "0", "6", "1,5")?
  const isSimpleNumericLatex = (latex) => {
    if (!latex) return false;
    const s = normalizeMinus(String(latex).replace(/\\,/g, " ").replace(/[{}]/g, "").trim());
    return /^[0-9−+\-., ]+$/.test(s);
  };

  // Parsowanie:
  //  - "\\dfrac{2}{5}" / "\\frac{2}{5}"
  //  - "1\\dfrac{1}{4}" / "-3\\frac{1}{2}"
  // Zwraca { whole, num, den } lub null.
const parseFractionLatex = (latexRaw) => {
  if (!latexRaw) return null;

  // parsujemy na surowym stringu, dopuszczamy Unicode minus "−"
  let s = String(latexRaw)
    .replace(/\\,/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Ujednolicamy minus do ASCII na czas parsowania
  const sParse = s.replace(/−/g, "-");

  // 1) liczba mieszana: "-3 \dfrac{1}{3}"
  const mixed = sParse.match(
    /^([+\-]?\d+)\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/
  );

  let whole = null;
  let fracPart = sParse;

  if (mixed) {
    whole = mixed[1];
    fracPart = mixed[2];
  } else {
    // 2) sam znak przed ułamkiem: "-\dfrac{1}{2}" albo "+\frac{1}{2}"
    const signOnly = sParse.match(
      /^([+\-])\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/
    );
    if (signOnly) {
      whole = signOnly[1];     // tu trzymamy sam znak jako "whole"
      fracPart = signOnly[2];
    }
  }

  const frac = fracPart.match(/\\(?:d)?frac\{([^}]+)\}\{([^}]+)\}/);
  if (!frac) return null;

  let num = String(frac[1]).replace(/[{}]/g, "").trim();
  let den = String(frac[2]).replace(/[{}]/g, "").trim();

  return {
    whole: whole != null ? normalizeMinus(String(whole).trim()) : null,
    num: normalizeMinus(num),
    den: normalizeMinus(den),
  };
};



  // Przybliżenie szerokości tekstu (bez pomiaru DOM)
  const approxTextW = (txt, fs) => {
    const t = String(txt ?? "");
    // 0.58 * fs na znak jest zwykle ok dla cyfr / prostych znaków
    return Math.max(0, t.length * fs * 0.58);
  };

  // Rysowanie ułamka jako SVG (licznik/kreska/mianownik), opcjonalnie z częścią całkowitą
  const FractionSvgLabel = ({ x, y, frac, fs = fontSize }) => {
    if (!frac) return null;

    const whole = frac.whole;
    const num = frac.num;
    const den = frac.den;

    const fsWhole = fs;
    const fsFrac = Math.max(11, Math.round(fs * 0.78)); // nie za małe na mobile

    const gap = Math.round(fs * 0.34); // odstęp między whole a ułamkiem
    const fracPad = Math.max(6, Math.round(fs * 0.22)); // padding na kreskę

    const wholeW = whole ? approxTextW(whole, fsWhole) : 0;
    const numW = approxTextW(num, fsFrac);
    const denW = approxTextW(den, fsFrac);

    const fracW = Math.max(numW, denW) + fracPad * 2;
    const totalW = whole ? wholeW + gap + fracW : fracW;

    // Układ wewnątrz grupy: zaczynamy od -totalW/2
    const startX = -totalW / 2;
    const fracXCenter = startX + (whole ? wholeW + gap + fracW / 2 : fracW / 2);

    // Pozycje pionowe w obrębie etykiety (bazujemy na "hanging")
    const numY = 0; // top
    const lineY = fsFrac * 1.05;
    const denY = fsFrac * 1.35;

    // Cały blok ma wysokość ~ denY + fsFrac
    return (
      <g transform={`translate(${x} ${y})`} style={{ pointerEvents: "none" }}>
        {whole && (
          <text
            x={startX}
            // Wyśrodkowanie pionowe względem całego "bloku" ułamka
            y={Math.round((denY + fsFrac) / 2)}
            textAnchor="start"
            dominantBaseline="middle"
            fontSize={fsWhole}
            fontFamily={fontFamily}
            fill="currentColor"
          >
            {whole}
          </text>
        )}


        {/* licznik */}
        <text
          x={fracXCenter}
          y={numY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {num}
        </text>

        {/* kreska ułamkowa */}
        <line
          x1={fracXCenter - fracW / 2}
          y1={lineY}
          x2={fracXCenter + fracW / 2}
          y2={lineY}
          stroke="currentColor"
          strokeWidth={Math.max(1.6, Math.round(fs * 0.08))}
          strokeLinecap="round"
        />

        {/* mianownik */}
        <text
          x={fracXCenter}
          y={denY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {den}
        </text>
      </g>
    );
  };

  // KaTeXowe podpisy: używamy foreignObject TYLKO dla nie-ułamków i nie-prostych rzeczy
  const leftLatexRaw = typeof labelLeftTex === "string" ? labelLeftTex : null;
  const rightLatexRaw = typeof labelRightTex === "string" ? labelRightTex : null;

  const leftFrac = parseFractionLatex(leftLatexRaw);
  const rightFrac = parseFractionLatex(rightLatexRaw);

  const leftIsSimple = isSimpleNumericLatex(leftLatexRaw);
  const rightIsSimple = isSimpleNumericLatex(rightLatexRaw);

  const leftUseForeign = !!leftLatexRaw && !leftFrac && !leftIsSimple;
  const rightUseForeign = !!rightLatexRaw && !rightFrac && !rightIsSimple;

  const boxW = (s) => Math.max(90, Math.min(240, 8 * (String(s || "").length || 0) + 28));
  const boxH = 52;

  // pozycje podpisów (dla "one" pokazujemy w punkcie styku – po obu stronach ten sam podpis)
  const labelLeftX = rootMode === "two" ? pL : rootMode === "one" ? pL : null;
  const labelRightX = rootMode === "two" ? pR : rootMode === "one" ? pR : null;

  // Wariant prosty do <text>
  const leftSimpleText =
    leftLatexRaw && leftIsSimple
      ? normalizeMinus(String(leftLatexRaw).replace(/\\,/g, " ").replace(/[{}]/g, "").trim())
      : null;

  const rightSimpleText =
    rightLatexRaw && rightIsSimple
      ? normalizeMinus(String(rightLatexRaw).replace(/\\,/g, " ").replace(/[{}]/g, "").trim())
      : null;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img">
      {/* oś X */}
      {showXAxis && (
        <>
          <line x1={leftX} y1={xAxisY} x2={rightX} y2={xAxisY} stroke={axisColor} strokeWidth={1.8} />
          <path
            d={`M ${rightX - AR} ${xAxisY - AR / 2} L ${rightX} ${xAxisY} L ${rightX - AR} ${xAxisY + AR / 2} Z`}
            fill={axisColor}
          />
        </>
      )}

      {/* ticki: 2 dla "two", 1 dla "one", 0 dla "none" */}
      {showZeroTicks && rootMode === "two" && (
        <>
          <line x1={pL} y1={xAxisY - tickLength} x2={pL} y2={xAxisY + tickLength} stroke={axisColor} strokeWidth={1.6} />
          <line x1={pR} y1={xAxisY - tickLength} x2={pR} y2={xAxisY + tickLength} stroke={axisColor} strokeWidth={1.6} />
        </>
      )}
      {showZeroTicks && rootMode === "one" && (
        <line x1={pL} y1={xAxisY - tickLength} x2={pL} y2={xAxisY + tickLength} stroke={axisColor} strokeWidth={1.6} />
      )}

      {/* podświetlenie (tylko gdy są dwa punkty rozdzielające) */}
      {highlightMode === "between" && <BandBetween />}
      {highlightMode === "outside" && <BandOutside />}

      {/* parabola */}
      <path d={d} fill="none" stroke={curveColor} strokeWidth={strokeWidth} />

      {/* podpis LEWY */}
      {labelLeftX != null && (
        <>
          {/* 1) ułamek → czyste SVG (stabilne na iOS) */}
          {leftFrac ? (
            <FractionSvgLabel x={labelLeftX} y={xAxisY + labelDy} frac={leftFrac} />
          ) : leftUseForeign ? (
            /* 2) złożone LaTeX → foreignObject (rzadkie przypadki) */
            <foreignObject
              x={labelLeftX - boxW(leftLatexRaw) / 2}
              y={xAxisY + labelDy - 6}
              width={boxW(leftLatexRaw)}
              height={boxH}
              pointerEvents="none"
            >
              <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
                <InlineMath math={leftLatexRaw} />
              </div>
            </foreignObject>
          ) : (
            /* 3) proste → <text> (liczby) */
            (leftSimpleText || plainLeft) && (
              <text
                x={labelLeftX}
                y={xAxisY + labelDy}
                textAnchor="middle"
                dominantBaseline="hanging"
                fontSize={fontSize}
                fontFamily={fontFamily}
                fill="currentColor"
                style={{ pointerEvents: "none" }}
              >
                {leftSimpleText || plainLeft}
              </text>
            )
          )}
        </>
      )}

      {/* podpis PRAWY */}
      {labelRightX != null && (
        <>
          {rightFrac ? (
            <FractionSvgLabel x={labelRightX} y={xAxisY + labelDy} frac={rightFrac} />
          ) : rightUseForeign ? (
            <foreignObject
              x={labelRightX - boxW(rightLatexRaw) / 2}
              y={xAxisY + labelDy - 6}
              width={boxW(rightLatexRaw)}
              height={boxH}
              pointerEvents="none"
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  width: "100%",
                  height: "100%",
                }}
              >
                <InlineMath math={rightLatexRaw} />
              </div>
            </foreignObject>
          ) : (
            (rightSimpleText || plainRight) && (
              <text
                x={labelRightX}
                y={xAxisY + labelDy}
                textAnchor="middle"
                dominantBaseline="hanging"
                fontSize={fontSize}
                fontFamily={fontFamily}
                fill="currentColor"
                style={{ pointerEvents: "none" }}
              >
                {rightSimpleText || plainRight}
              </text>
            )
          )}
        </>
      )}
    </svg>
  );
}
